import { Affix, Button, Form, Input, Select, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ModalConfirm from '../../../../components/Shared/modal/confirm';
import ModalDiscardChanges from '../../../../components/Shared/modal/discardChanges';
import useNotify from '../../../../context/Notification/useNotify';
import DataFlowService from '../../../../services/DataFlow.service';
import { useBlocker } from '../../../../util/usePrompt';
import { ROUTER } from '../../../../constants/router';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;

export default function Tab5({ remarks, getDataFlowDetail }) {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const { businessProcessId } = useParams();
    const navigate = useNavigate();

    const notify = useNotify();

    // Confirm
    const [openConfirm, setOpenConfirm] = useState(false);
    const [valueSave, setValueSave] = useState();
    const [loading, setLoading] = useState(false);

    // Discard change
    const [change, setChange] = useState(false);
    const [openChange, setOpenChange] = useState(false);
    const [nextLocation, setNextLocation] = useState(null);
    const [isChangeLocation, setIsChangeLocation] = useState(false);

    useBlocker((nextLocation) => onChangePage(nextLocation), change === true);

    const onChangePage = (nextLocation) => {
        setOpenChange(true);
        setIsChangeLocation(true);
        setNextLocation(nextLocation);
    };

    const handelOk = async () => {
        setOpenChange(false);
        setChange(false);
    };

    const handelOkLocation = async () => {
        setOpenChange(false);
        setChange(false);
        setIsChangeLocation(false);
        await form.resetFields();
        await navigate(
            nextLocation.location.pathname + nextLocation.location.search
        );
        await getDataFlowDetail(businessProcessId);
    };

    const changePage = () => {
        navigate(
            `${ROUTER.DS_INVENTORY.DATAFLOW}/${businessProcessId}/view?tab=6&mode=view`
        );
    };

    // Discard change end

    const onFinish = () => {
        createRemarks({
            businessProcessId,
            ...valueSave,
        });
    };

    const createRemarks = async (payload) => {
        setLoading(true);
        try {
            const { data } = await DataFlowService.createRemarks(payload);
            if (data?.resultCode === 1) {
                notify({
                    title: t('common.Success'),
                    description: t('common.SaveData'),
                });
                setOpenConfirm(false);
                setOpenChange(false);
                setChange(false);
                getDataFlowDetail(businessProcessId);
                setLoading(false);
                changePage();
            }
        } catch (error) {
            setLoading(false);
            console.log(error);
            notify({
                type: 'error',
                title: t('common.Fail'),
                description: t('Failed to submitted'),
            });
            setOpenConfirm(false);
        }
    };

    useEffect(() => {
        form.setFieldsValue({
            ...remarks,
        });
    }, [remarks, form]);

    return (
        <Form
            scrollToFirstError={{
                behavior: 'smooth',
                block: 'center',
            }}
            layout="vertical"
            form={form}
            onFinish={(value) => {
                setOpenConfirm(true);
                setValueSave(value);
            }}
            onFieldsChange={(_, allFields) => {
                if (allFields.length > 0) {
                    setChange(true);
                }
            }}
        >
            <Affix offsetTop={60}>
                <div className="flex justify-end space-x-3">
                    <Button
                        size="large"
                        type="default"
                        onClick={changePage}
                        className="text-purple-PHX border-purple-PHX w-32"
                    >
                        {t('common.Cancel')}
                    </Button>
                    <Button
                        size="large"
                        type="primary"
                        className="w-32"
                        htmlType="submit"
                    >
                        {t('common.Save')}
                    </Button>
                </div>
            </Affix>
            <Form.Item
                name="remarksDetail"
                label={
                    <span className="text-[21px] font-bold">
                        {t('dataFlow.remarks')}
                    </span>
                }
            >
                <TextArea
                    size="large"
                    rows={4}
                    placeholder={t('dataFlow.inputpl3')}
                />
            </Form.Item>
            <div className="grid grid-cols-2">
                <div className="col-span-1">
                    <Form.Item
                        name="isDpa"
                        label={
                            <>
                                <span className="text-[21px] font-bold">
                                    {t('Has a DPA been created')}
                                </span>
                                <Tooltip
                                    placement="right"
                                    overlayClassName="min-w-[320px]"
                                    title={
                                        <div
                                            className="whitespace-pre-line"
                                            dangerouslySetInnerHTML={{
                                                __html: t(
                                                    'Has a DPA been created tooltip'
                                                ),
                                            }}
                                        ></div>
                                    }
                                >
                                    <a className="bg-purple-light p-2 flex items-center justify-center rounded-lg text-purple ml-2">
                                        <ExclamationCircleOutlined />
                                    </a>
                                </Tooltip>
                            </>
                        }
                    >
                        <Select
                            size="large"
                            allowClear
                            placeholder={t('Please select')}
                            options={[
                                {
                                    label: t('Yes'),
                                    value: true,
                                },
                                {
                                    label: t('No_'),
                                    value: false,
                                },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        name="isDpia"
                        label={
                            <>
                                <span className="text-[21px] font-bold">
                                    {t('Has a DPIA been created')}
                                </span>
                                <Tooltip
                                    placement="right"
                                    overlayClassName="min-w-[320px]"
                                    title={
                                        <div
                                            className="whitespace-pre-line"
                                            dangerouslySetInnerHTML={{
                                                __html: t(
                                                    'Has a DPIA been created tooltip'
                                                ),
                                            }}
                                        ></div>
                                    }
                                >
                                    <a className="bg-purple-light p-2 flex items-center justify-center rounded-lg text-purple ml-2">
                                        <ExclamationCircleOutlined />
                                    </a>
                                </Tooltip>
                            </>
                        }
                    >
                        <Select
                            size="large"
                            allowClear
                            placeholder={t('Please select')}
                            options={[
                                {
                                    label: t('Yes'),
                                    value: true,
                                },
                                {
                                    label: t('No_'),
                                    value: false,
                                },
                            ]}
                        />
                    </Form.Item>
                </div>
            </div>
            <ModalDiscardChanges
                open={openChange}
                handelOk={isChangeLocation ? handelOkLocation : handelOk}
                handelClose={() => setOpenChange(false)}
            />
            <ModalConfirm
                loading={loading}
                text={t('Do you want to confirm')}
                open={openConfirm}
                handelOk={onFinish}
                handelClose={() => setOpenConfirm(false)}
            />
        </Form>
    );
}
